import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { DataService } from '../data/data.service';

/** source-service sync endpoints return HTTP 200 with a plain-text body; Angular surfaces the JSON parse failure as HttpErrorResponse with status 200. */
function recoverPlainTextOk<T>() {
  return (source: Observable<T>): Observable<T | { success: true; message: string }> =>
    source.pipe(
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 200) {
          const body: string = typeof err.error === 'string'
            ? err.error
            : (err.error?.text ?? '');
          return of({ success: true as const, message: body });
        }
        return throwError(() => err);
      })
    );
}

/**
 * Centralised REST API layer for integration endpoints on source-service
 * (environment.apiUrlSourceService).
 *
 * Covers:
 *  - Integration setup flows  → /integration-setups/...
 *  - Setup flow steps         → /integration-setups/flows/...
 *  - Save connector studio    → /integration-setups/connector-studio/...
 *  - Re-authentication        → /integration-setups/flows/init-re-auth/...
 *  - Runtime sync             → /integrations/...
 */
@Injectable({
  providedIn: 'root'
})
export class IntegrationSourceService {
  private baseUrl = environment.apiUrlSourceService;

  constructor(private dataService: DataService) {}

  // ──────────────────────────────────────────────
  //  INTEGRATION SETUP FLOWS (CREATE / EDIT)
  // ──────────────────────────────────────────────

  /**
   * Initialise a CREATE integration setup flow.
   * GET /integration-setups/flows/init
   */
  initSetupFlow(userId: number, sourceType: string, productId: number = 1): Observable<any> {
    return this.dataService.get({
      url: `${this.baseUrl}/integration-setups/flows/init?userId=${userId}&dataSourceType=${sourceType}&productId=${productId}`
    });
  }

  /**
   * Initialise an EDIT integration setup flow.
   * GET /integration-setups/flows/init-edit/integrations/{sourceId}
   */
  initEditFlow(sourceId: number | string, userId: number, dataSourceType: string, productId: number = 1): Observable<any> {
    return this.dataService.get({
      url: `${this.baseUrl}/integration-setups/flows/init-edit/integrations/${sourceId}?userId=${userId}&dataSourceType=${dataSourceType}&productId=${productId}`
    });
  }

  /**
   * Initialise a CREATE flow with a template (attach mode).
   * GET /integration-setups/flows/init-with-template
   */
  initFlowWithTemplate(userId: number, dataSourceType: string, templateId: string | number): Observable<any> {
    return this.dataService.get({
      url: `${this.baseUrl}/integration-setups/flows/init-with-template`,
      param: {
        userId: userId.toString(),
        dataSourceType,
        templateId: templateId.toString()
      }
    });
  }

  // ──────────────────────────────────────────────
  //  SETUP FLOW STEPS (NEXT / PREVIOUS)
  // ──────────────────────────────────────────────

  /**
   * Get next step in the setup flow.
   * POST /integration-setups/flows/next
   */
  getNextStep(sessionState: string, currentStepUserInput: any, userId: number, productId: number = 1): Observable<any> {
    return this.dataService.post({
      url: `${this.baseUrl}/integration-setups/flows/next?productId=${productId}`,
      data: { sessionState, currentStepUserInput, userId }
    });
  }

  /**
   * Get previous step in the setup flow.
   * POST /integration-setups/flows/previous
   */
  getPreviousStep(sessionState: string, userId: number, productId: number = 1): Observable<any> {
    return this.dataService.post({
      url: `${this.baseUrl}/integration-setups/flows/previous?productId=${productId}`,
      data: { sessionState, userId }
    });
  }

  // ──────────────────────────────────────────────
  //  SAVE CONNECTOR STUDIO SOURCE
  // ──────────────────────────────────────────────

  /**
   * Save a new connector-studio source (CREATE).
   * POST /integration-setups/connector-studio/sources
   */
  saveConnectorStudioSource(data: any, userId: number, sourceType: string, productId: number = 1): Observable<any> {
    return this.dataService.post({
      url: `${this.baseUrl}/integration-setups/connector-studio/sources?userId=${userId}&dataSourceType=${sourceType}&productId=${productId}`,
      data
    });
  }

  /**
   * Save an edited connector-studio source (EDIT).
   * POST /integration-setups/connector-studio/sources/{sourceId}
   */
  saveEditedConnectorStudioSource(data: any, sourceId: number | string, userId: number, productId: number = 1): Observable<any> {
    return this.dataService.post({
      url: `${this.baseUrl}/integration-setups/connector-studio/sources/${sourceId}?userId=${userId}&productId=${productId}`,
      data
    });
  }

  /**
   * Save an edited connector-studio source as draft (EDIT LATER).
   * PUT /integration-setups/connector-studio/sources/{sourceId}/draft
   * Note: source-service /draft endpoint uses PUT (idempotent upsert of draft state),
   * unlike the old service which uses POST for save-edit-connector-studio-source-later.
   */
  saveEditedConnectorStudioSourceLater(data: any, sourceId: number | string, userId: number, productId: number = 1): Observable<any> {
    return this.dataService.put({
      url: `${this.baseUrl}/integration-setups/connector-studio/sources/${sourceId}/draft?userId=${userId}&productId=${productId}`,
      data
    });
  }

  // ──────────────────────────────────────────────
  //  RE-AUTHENTICATION
  // ──────────────────────────────────────────────

  /**
   * Initialise re-auth flow.
   * GET /integration-setups/flows/init-re-auth/integrations/{integrationId}
   */
  initReAuth(integrationId: number | string, userId: number, dataSourceType: string, productId: number = 1): Observable<any> {
    return this.dataService.get({
      url: `${this.baseUrl}/integration-setups/flows/init-re-auth/integrations/${integrationId}`,
      param: {
        userId: userId.toString(),
        dataSourceType,
        productId: productId.toString()
      }
    });
  }

  /**
   * Complete re-auth via connector studio.
   * POST /integration-setups/connector-studio/sources/re-auth/integrations/{integrationId}
   */
  reAuthConnectorStudio(
    integrationId: number | string,
    userId: number | undefined,
    sessionState: string,
    eqp: string,
    productId: number | undefined = 1
  ): Observable<any> {
    const params: any = {};
    if (userId !== undefined) { params.userId = userId.toString(); }
    if (productId !== undefined) { params.productId = productId.toString(); }

    return this.dataService.post({
      url: `${this.baseUrl}/integration-setups/connector-studio/sources/re-auth/integrations/${integrationId}`,
      param: params,
      data: {
        sessionState,
        currentStepUserInput: { eqp },
        userId
      }
    });
  }

  // ──────────────────────────────────────────────
  //  RUNTIME – SYNC
  // ──────────────────────────────────────────────

  /**
   * Sync integrations.
   * POST /integrations/sync-integrations
   */
  syncIntegrations(integrationIds: number[], initiatedBy: number, productId: number = 1): Observable<any> {
    return this.dataService.post({
      url: `${this.baseUrl}/integrations/sync-integrations`,
      param: { productId: productId.toString() },
      data: { integrationIds, initiatedBy }
    }).pipe(recoverPlainTextOk());
  }

  /**
   * Sync specific tables.
   * POST /integrations/sync-tables
   */
  syncTables(tableName: string, integrationIds: number[], initiatedBy: number = 0, productId: number = 1): Observable<any> {
    return this.dataService.post({
      url: `${this.baseUrl}/integrations/sync-tables`,
      param: { productId: productId.toString() },
      data: { tableName, integrationIds, initiatedBy }
    }).pipe(recoverPlainTextOk());
  }
}
